import React, { useState } from 'react';
import { BarChart3, ArrowRightLeft, Percent, Calculator, Hash } from 'lucide-react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { parseFloatOrNaN } from '../../utils/gpaLogic';
import { toast } from 'react-toastify';

const PercentageCalculator = () => {
  const [marks, setMarks] = useState({ obtained: '', total: '' });
  const [pctToMarks, setPctToMarks] = useState({ pct: '', base: '' });
  
  const [marksResult, setMarksResult] = useState(null);
  const [pctResult, setPctResult] = useState(null);

  const calcPercentage = () => {
    const obt = parseFloatOrNaN(marks.obtained);
    const tot = parseFloatOrNaN(marks.total);
    if (!Number.isFinite(obt) || !Number.isFinite(tot) || tot <= 0) return toast.error("Invalid marks.");
    setMarksResult((obt / tot) * 100);
    toast.success("Calculated!");
  };

  const calcMarks = () => {
    const p = parseFloatOrNaN(pctToMarks.pct);
    const b = parseFloatOrNaN(pctToMarks.base);
    if (!Number.isFinite(p) || !Number.isFinite(b) || b < 0) return toast.error("Invalid percentage or base.");
    setPctResult((p / 100) * b);
    toast.success("Calculated!");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-700">
      {/* Marks to Percentage */}
      <div className="bg-white dark:bg-[#0f1535] p-8 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm transition-all hover:shadow-xl hover:shadow-indigo-500/5 group">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 rounded-2xl group-hover:scale-110 transition-transform">
            <Percent size={22} />
          </div>
          <div>
            <h3 className="font-black text-slate-900 dark:text-white tracking-tight uppercase text-xs">Convert to %</h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Marks &rarr; Percentage</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Obtained</label>
              <Input 
                value={marks.obtained} 
                onChange={(e) => setMarks({...marks, obtained: e.target.value})} 
                placeholder="0"
                className="text-center font-bold h-12"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Total</label>
              <Input 
                value={marks.total} 
                onChange={(e) => setMarks({...marks, total: e.target.value})} 
                placeholder="0"
                className="text-center font-bold h-12"
                inputMode="decimal"
              />
            </div>
          </div>

          <Button onClick={calcPercentage} className="w-full py-4 rounded-2xl shadow-lg shadow-indigo-500/10" icon={Calculator}>
            Calculate Percentage
          </Button>

          {marksResult !== null && (
            <div className="mt-4 p-8 bg-slate-50 dark:bg-white/5 rounded-[2rem] border-2 border-indigo-500/10 text-center animate-in zoom-in-95">
              <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-2">Result</span>
              <div className="inline-flex items-center gap-2">
                <span className="text-5xl font-black text-slate-900 dark:text-white">{marksResult.toFixed(2)}</span>
                <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Percentage to Marks */}
      <div className="bg-white dark:bg-[#0f1535] p-8 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm transition-all hover:shadow-xl hover:shadow-indigo-500/5 group">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-purple-50 dark:bg-purple-500/10 text-purple-600 rounded-2xl group-hover:scale-110 transition-transform">
            <Hash size={22} />
          </div>
          <div>
            <h3 className="font-black text-slate-900 dark:text-white tracking-tight uppercase text-xs">Convert to Marks</h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Percentage &rarr; Marks</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Given %</label>
              <Input 
                value={pctToMarks.pct} 
                onChange={(e) => setPctToMarks({...pctToMarks, pct: e.target.value})} 
                placeholder="0"
                className="text-center font-bold h-12"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Out Of</label>
              <Input 
                value={pctToMarks.base} 
                onChange={(e) => setPctToMarks({...pctToMarks, base: e.target.value})} 
                placeholder="0"
                className="text-center font-bold h-12"
                inputMode="decimal"
              />
            </div>
          </div>

          <Button onClick={calcMarks} variant="secondary" className="w-full py-4 rounded-2xl shadow-lg shadow-purple-500/5" icon={ArrowRightLeft}>
            Calculate Marks
          </Button>

          {pctResult !== null && (
            <div className="mt-4 p-8 bg-slate-50 dark:bg-white/5 rounded-[2rem] border-2 border-purple-500/10 text-center animate-in zoom-in-95">
              <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-2">Result Marks</span>
              <span className="text-5xl font-black text-slate-900 dark:text-white">
                {pctResult % 1 === 0 ? pctResult : pctResult.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PercentageCalculator;
